/**
 * Features MVI Reducer
 *
 * Pure function: (state, intent) => newState.
 * No I/O, no side effects.
 * app-shell-work-context-R04/R07; business-model-features-R08/R11.
 */

import type { IFeaturesState } from "./state";
import type { TFeaturesIntent } from "./intents";

export function featuresReducer(
  state: IFeaturesState,
  intent: TFeaturesIntent,
): IFeaturesState {
  switch (intent.type) {
    // app-shell-work-context-R07; frontend-screens-app-shell-R12.
    case "CONTEXT_PENDING":
      return { ...state, contextPending: true, selectedFeatureId: "" };
    case "CONTEXT_VERIFIED":
      return {
        ...state,
        contextPending: false,
        projectId: intent.projectId,
        selectedFeatureId: intent.featureId,
        ...(state.projectId === intent.projectId ? {} : {
          features: [], featuresWithArchived: [], retainedFeature: null,
        }),
      };
    case "LOAD_FEATURES_START":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "LOAD_FEATURES_CANCELLED":
      return { ...state, isLoading: false };

    case "LOAD_FEATURES_SUCCESS": {
      // Выбор фичи принадлежит выбранному проекту
      // (business-model-features-R10): выбранной может быть только фича
      // перечня текущего проекта. Поэтому пришедший перечень — единственное
      // основание для выбора, а прежний выбор сохраняется, только если он в
      // этом перечне есть.
      const isSameProject = state.projectId === intent.projectId;
      // Архивная фича держится в селекторе, только пока она выбрана, и это
      // единственное исключение из принадлежности перечню — ограниченное
      // своим проектом (R08, R11). Перечень другого проекта его снимает.
      const candidate = intent.retainedFeature ?? state.retainedFeature;
      const retainedFeature =
        isSameProject &&
        candidate !== null && candidate.projectId === intent.projectId &&
        candidate.id === state.selectedFeatureId
          ? candidate
          : null;
      const selectionBelongs =
        isSameProject &&
        (retainedFeature !== null ||
          intent.features.some(
            (feature) => feature.id === state.selectedFeatureId,
          ));

      return {
        ...state,
        projectId: intent.projectId,
        features: intent.features,
        isLoading: false,
        error: null,
        // Выбор вне перечня — смена проекта, удаление или архивация чужими
        // руками — пересчитывается на новейшую HEAD перечня (первую, поскольку
        // перечень идёт от новых к старым); пустой перечень очищает выбор
        // (R10, R01). Состояния «активна фича, которой нет в перечне» не
        // остаётся ни на мгновение, поэтому содержимого по чужой фиче продукт
        // не запрашивает.
        selectedFeatureId: selectionBelongs
          ? state.selectedFeatureId
          : (intent.features[0]?.id ?? ""),
        retainedFeature,
      };
    }

    case "LOAD_FEATURES_ERROR":
      return {
        ...state,
        isLoading: false,
        error: intent.error,
      };

    case "SELECT_FEATURE":
      return {
        ...state,
        selectedFeatureId: intent.featureId,
        // Смена выбора убирает архивную фичу из списка опций окончательно (R11).
        retainedFeature:
          state.retainedFeature?.id === intent.featureId
            ? state.retainedFeature
            : null,
      };

    case "CREATE_FEATURE_START":
      return {
        ...state,
        isCreating: true,
        error: null,
      };

    case "CREATE_FEATURE_SUCCESS":
      return {
        ...state,
        projectId: intent.feature.projectId,
        features: [...state.features, intent.feature],
        // Выбранной становится созданная фича (feature-lifecycle-R03).
        selectedFeatureId: intent.feature.id,
        retainedFeature: null,
        isCreating: false,
        error: null,
      };

    case "CREATE_FEATURE_ERROR":
      return {
        ...state,
        isCreating: false,
        error: intent.error,
      };

    case "LOAD_WITH_ARCHIVED_SUCCESS":
      return {
        ...state,
        featuresWithArchived: intent.features,
        isLoading: false,
        error: null,
      };

    case "UPDATE_FEATURE_START":
      return {
        ...state,
        isSaving: true,
        error: null,
      };

    case "UPDATE_FEATURE_SUCCESS":
      return {
        ...state,
        features: state.features.map((feature) =>
          feature.id === intent.feature.id ? intent.feature : feature,
        ),
        // Переименование выбранной фичи выбора не меняет: она остаётся
        // выбранной под новым названием (feature-lifecycle-R08). Архивация
        // выбранной фичи убирает её из перечня, но оставляет в селекторе
        // (business-model-features-R08, R11).
        retainedFeature:
          state.selectedFeatureId === intent.feature.id &&
          intent.feature.status === "archived"
            ? intent.feature
            : null,
        isSaving: false,
        error: null,
      };

    case "UPDATE_FEATURE_ERROR":
      return {
        ...state,
        isSaving: false,
        error: intent.error,
      };

    case "DELETE_FEATURE_START":
      return {
        ...state,
        isDeleting: true,
        error: null,
      };

    case "DELETE_FEATURE_SUCCESS":
      return {
        ...state,
        features: state.features.filter(
          (feature) => feature.id !== intent.featureId,
        ),
        // Удалена выбранная фича — выбор снимается (feature-lifecycle-R19).
        // Новую выбирает автоматический выбор при перечитывании перечня:
        // новейшая HEAD-фича, а если фич не осталось — контекст пуст.
        selectedFeatureId:
          state.selectedFeatureId === intent.featureId
            ? ""
            : state.selectedFeatureId,
        retainedFeature:
          state.retainedFeature?.id === intent.featureId
            ? null
            : state.retainedFeature,
        isDeleting: false,
        error: null,
      };

    case "DELETE_FEATURE_ERROR":
      return {
        ...state,
        isDeleting: false,
        error: intent.error,
      };

    case "CLEAR_FEATURES":
      return {
        ...state,
        // Сброс выбора проекта очищает перечень и выбор фичи (R10): вместе с
        // ними снимается и признак проекта, чей перечень держало состояние.
        projectId: "",
        features: [],
        featuresWithArchived: [],
        selectedFeatureId: "",
        retainedFeature: null,
        error: null,
      };

    default:
      return state;
  }
}
